import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Users,
  UserPlus,
  Calendar,
  DollarSign,
  Gift,
  CheckCircle,
  AlertCircle,
  Loader2,
  Target,
  Handshake,
  Star,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useReferralCampaign,
  useCreateReferralCampaign,
  useUpdateReferralCampaign,
  useToggleReferralCampaignStatus
} from "@/api-services/hooks/useReferralCampaigns";
import {
  ReferralCampaignForm,
  ReferralCampaignFormErrors,
  REWARD_TYPES
} from "@/api-services/hooks/referralCampaignTypes";

const AddEditReferralCampaignPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const referralCampaignId = parseInt(id || "0");

  // Form state
  const [formData, setFormData] = useState<ReferralCampaignForm>({
    referralCampaignName: "",
    referrerRewardType: "DISCOUNT",
    referrerRewardValue: 10,
    referredRewardType: "DISCOUNT",
    referredRewardValue: 5,
    validityDays: 30,
    maxUsesPerReferrer: 10,
    isActive: true,
  });

  // Validation state
  const [errors, setErrors] = useState<ReferralCampaignFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  // Fetch existing referral campaign data for edit mode
  const { data: existingData, loading, error } = useReferralCampaign(
    referralCampaignId,
    {
      enabled: isEdit && !!referralCampaignId && referralCampaignId > 0,
      onError: (err) => {
        toast.error(`Failed to load referral campaign data: ${err.message}`);
      },
    }
  );

  // Create/Update mutations
  const { mutate: createReferralCampaign, isPending: isCreating } = useCreateReferralCampaign({
    onSuccess: () => {
      toast.success("Referral campaign created successfully!");
      navigate("/admin/referral-campaigns");
    },
    onError: (error) => {
      toast.error(`Failed to create referral campaign: ${error.message}`);
    },
  });

  const { mutate: updateReferralCampaign, isPending: isUpdating } = useUpdateReferralCampaign({
    onSuccess: () => {
      toast.success("Referral campaign updated successfully!");
      navigate("/admin/referral-campaigns");
    },
    onError: (error) => {
      toast.error(`Failed to update referral campaign: ${error.message}`);
    },
  });

  // Toggle active status hook
  const { mutate: toggleStatus, isPending: isTogglingStatus } = useToggleReferralCampaignStatus({
    onSuccess: (data) => {
      toast.success("Referral campaign status updated successfully!");
      // Update form data with new status from API response
      if (data?.data) {
        setFormData(prev => ({ ...prev, isActive: data.data.isActive }));
      }
    },
    onError: (error) => {
      toast.error(`Failed to update referral campaign status: ${error.message}`);
    },
  });

  // Initialize form with existing data for edit mode
  useEffect(() => {
    if (existingData && isEdit) {
      const formData: ReferralCampaignForm = {
        referralCampaignName: existingData.referralCampaignName || "",
        referrerRewardType: existingData.referrerRewardType,
        referrerRewardValue: existingData.referrerRewardValue,
        referredRewardType: existingData.referredRewardType,
        referredRewardValue: existingData.referredRewardValue,
        validityDays: existingData.validityDays,
        maxUsesPerReferrer: existingData.maxUsesPerReferrer,
        isActive: existingData.isActive,
      };
      setFormData(formData);
    }
  }, [existingData, isEdit]);

  // Validation
  const validateForm = useCallback((): boolean => {
    const newErrors: ReferralCampaignFormErrors = {};

    if (!formData.referralCampaignName?.trim()) {
      newErrors.referralCampaignName = "Campaign name is required";
    }

    if (!formData.referrerRewardType) {
      newErrors.referrerRewardType = "Referrer reward type is required";
    }

    if (!formData.referrerRewardValue || formData.referrerRewardValue <= 0) {
      newErrors.referrerRewardValue = "Referrer reward value must be greater than 0";
    } else if (formData.referrerRewardType === 'DISCOUNT' && formData.referrerRewardValue > 100) {
      newErrors.referrerRewardValue = "Discount cannot exceed 100%";
    }

    if (!formData.referredRewardType) {
      newErrors.referredRewardType = "Referred reward type is required";
    }

    if (!formData.referredRewardValue || formData.referredRewardValue <= 0) {
      newErrors.referredRewardValue = "Referred reward value must be greater than 0";
    } else if (formData.referredRewardType === 'DISCOUNT' && formData.referredRewardValue > 100) {
      newErrors.referredRewardValue = "Discount cannot exceed 100%";
    }

    if (!formData.validityDays || formData.validityDays <= 0) {
      newErrors.validityDays = "Validity days must be greater than 0";
    } else if (formData.validityDays > 365) {
      newErrors.validityDays = "Validity days cannot exceed 365";
    }

    if (!formData.maxUsesPerReferrer || formData.maxUsesPerReferrer <= 0) {
      newErrors.maxUsesPerReferrer = "Maximum uses per referrer must be greater than 0";
    } else if (formData.maxUsesPerReferrer > 1000) {
      newErrors.maxUsesPerReferrer = "Maximum uses per referrer cannot exceed 1000";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle form input changes
  const handleInputChange = useCallback((field: keyof ReferralCampaignForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setIsDirty(true);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    if (isEdit) {
      updateReferralCampaign({
        id: referralCampaignId,
        data: formData,
      });
    } else {
      createReferralCampaign(formData);
    }
  }, [formData, referralCampaignId, isEdit, validateForm, updateReferralCampaign, createReferralCampaign]);

  // Reset form to original values
  const handleReset = useCallback(() => {
    if (isEdit && existingData) {
      const formData: ReferralCampaignForm = {
        referralCampaignName: existingData.referralCampaignName || "",
        referrerRewardType: existingData.referrerRewardType,
        referrerRewardValue: existingData.referrerRewardValue,
        referredRewardType: existingData.referredRewardType,
        referredRewardValue: existingData.referredRewardValue,
        validityDays: existingData.validityDays,
        maxUsesPerReferrer: existingData.maxUsesPerReferrer,
        isActive: existingData.isActive,
      };
      setFormData(formData);
    } else {
      // Reset to default values
      setFormData({
        referralCampaignName: "",
        referrerRewardType: "DISCOUNT",
        referrerRewardValue: 10,
        referredRewardType: "DISCOUNT",
        referredRewardValue: 5,
        validityDays: 30,
        maxUsesPerReferrer: 10,
        isActive: true,
      });
    }
    setErrors({});
    setIsDirty(false);
  }, [isEdit, existingData]);

  // Handle navigation
  const handleBack = useCallback(() => {
    if (isDirty) {
      setShowLeaveModal(true);
    } else {
      navigate("/admin/referral-campaigns");
    }
  }, [isDirty, navigate]);

  const isLoading = loading || isCreating || isUpdating || isTogglingStatus;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Referral Campaigns
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              {isEdit ? "Edit Referral Campaign" : "Create New Referral Campaign"}
            </h1>
            <p className="text-muted-foreground mt-1">
              {isEdit
                ? "Modify the referral campaign details"
                : "Create a new referral campaign to encourage user referrals"
              }
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!isDirty || isLoading}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isDirty || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEdit ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? "Update Campaign" : "Create Campaign"}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Referrer Rewards */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Referrer Rewards
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Referrer Reward Type */}
              <div className="space-y-2">
                <Label htmlFor="referrerRewardType" className="flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  Referrer Reward Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.referrerRewardType}
                  onValueChange={(value) => handleInputChange("referrerRewardType", value)}
                >
                  <SelectTrigger className={errors.referrerRewardType ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select reward type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REWARD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.referrerRewardType && (
                  <p className="text-sm text-red-500">{errors.referrerRewardType}</p>
                )}
              </div>

              {/* Referrer Reward Value */}
              <div className="space-y-2">
                <Label htmlFor="referrerRewardValue">Referrer Reward Value <span className="text-red-500">*</span></Label>
                <Input
                  id="referrerRewardValue"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={formData.referrerRewardType === 'DISCOUNT' ? 100 : undefined}
                  value={formData.referrerRewardValue}
                  onChange={(e) => handleInputChange("referrerRewardValue", parseFloat(e.target.value) || 0)}
                  className={errors.referrerRewardValue ? "border-red-500" : ""}
                />
                {errors.referrerRewardValue && (
                  <p className="text-sm text-red-500">{errors.referrerRewardValue}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  {formData.referrerRewardType === 'DISCOUNT' && 'Percentage discount (e.g., 10 for 10%)'}
                  {formData.referrerRewardType === 'CASHBACK' && 'Cashback amount in dollars'}
                  {formData.referrerRewardType === 'CREDIT' && 'Account credit amount in dollars'}
                  {formData.referrerRewardType === 'FREE_RIDE' && 'Number of free rides'}
                  {formData.referrerRewardType === 'POINTS' && 'Loyalty points to award'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Referred User Rewards */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Referred User Rewards
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Referred Reward Type */}
              <div className="space-y-2">
                <Label htmlFor="referredRewardType" className="flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  Referred User Reward Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.referredRewardType}
                  onValueChange={(value) => handleInputChange("referredRewardType", value)}
                >
                  <SelectTrigger className={errors.referredRewardType ? "border-red-500" : ""}>
                    <SelectValue placeholder="Select reward type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REWARD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.referredRewardType && (
                  <p className="text-sm text-red-500">{errors.referredRewardType}</p>
                )}
              </div>

              {/* Referred Reward Value */}
              <div className="space-y-2">
                <Label htmlFor="referredRewardValue">Referred User Reward Value <span className="text-red-500">*</span></Label>
                <Input
                  id="referredRewardValue"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={formData.referredRewardType === 'DISCOUNT' ? 100 : undefined}
                  value={formData.referredRewardValue}
                  onChange={(e) => handleInputChange("referredRewardValue", parseFloat(e.target.value) || 0)}
                  className={errors.referredRewardValue ? "border-red-500" : ""}
                />
                {errors.referredRewardValue && (
                  <p className="text-sm text-red-500">{errors.referredRewardValue}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  {formData.referredRewardType === 'DISCOUNT' && 'Percentage discount (e.g., 5 for 5%)'}
                  {formData.referredRewardType === 'CASHBACK' && 'Cashback amount in dollars'}
                  {formData.referredRewardType === 'CREDIT' && 'Account credit amount in dollars'}
                  {formData.referredRewardType === 'FREE_RIDE' && 'Number of free rides'}
                  {formData.referredRewardType === 'POINTS' && 'Loyalty points to award'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Settings */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Campaign Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Campaign Name */}
              <div className="space-y-2">
                <Label htmlFor="referralCampaignName" className="flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Campaign Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="referralCampaignName"
                  type="text"
                  value={formData.referralCampaignName}
                  onChange={(e) => handleInputChange("referralCampaignName", e.target.value)}
                  placeholder="Enter campaign name"
                  className={errors.referralCampaignName ? "border-red-500" : ""}
                />
                {errors.referralCampaignName && (
                  <p className="text-sm text-red-500">{errors.referralCampaignName}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Validity Days */}
                <div className="space-y-2">
                  <Label htmlFor="validityDays" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Validity Period (Days) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="validityDays"
                    type="number"
                    min="1"
                    max="365"
                    value={formData.validityDays}
                    onChange={(e) => handleInputChange("validityDays", parseInt(e.target.value) || 0)}
                    className={errors.validityDays ? "border-red-500" : ""}
                  />
                  {errors.validityDays && (
                    <p className="text-sm text-red-500">{errors.validityDays}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Number of days the referral reward remains valid
                  </p>
                </div>

                {/* Maximum Uses Per Referrer */}
                <div className="space-y-2">
                  <Label htmlFor="maxUsesPerReferrer">Maximum Uses Per Referrer <span className="text-red-500">*</span></Label>
                  <Input
                    id="maxUsesPerReferrer"
                    type="number"
                    min="1"
                    max="1000"
                    value={formData.maxUsesPerReferrer}
                    onChange={(e) => handleInputChange("maxUsesPerReferrer", parseInt(e.target.value) || 0)}
                    className={errors.maxUsesPerReferrer ? "border-red-500" : ""}
                  />
                  {errors.maxUsesPerReferrer && (
                    <p className="text-sm text-red-500">{errors.maxUsesPerReferrer}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    Maximum number of successful referrals per referrer
                  </p>
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-muted-foreground" />
                  <Label htmlFor="isActive" className="cursor-pointer">
                    Active Status
                  </Label>
                </div>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  disabled={isTogglingStatus || !isEdit}
                  onCheckedChange={(checked) => {
                    if (isEdit && referralCampaignId > 0) {
                      toggleStatus(referralCampaignId);
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </form>

      {/* Unsaved Changes Modal */}
      <Dialog open={showLeaveModal} onOpenChange={setShowLeaveModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowLeaveModal(false)}>
              Stay on Page
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setShowLeaveModal(false);
                navigate("/admin/referral-campaigns");
              }}
            >
              Leave Without Saving
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddEditReferralCampaignPage;